const asArray = (value) => (Array.isArray(value) ? value : []);

const firstArray = (...values) => values.find((value) => Array.isArray(value) && value.length) || [];

const toTimedLyricArray = (value) => {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== 'object') return [];
  return firstArray(value.lines, value.lyrics, value.items, value.segments, value.entries);
};

const firstTimedLyricArray = (...values) => values.map(toTimedLyricArray).find((value) => value.length) || [];

const toText = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value.trim();
  return String(value.title || value.name || value.concept || value.term || value.text || value.line || value.lyric || value.lyrics || value.content || value.label || '').trim();
};

const normalizeConcept = (value) => {
  const title = toText(value);
  if (!title) return null;
  const detail = typeof value === 'object' && value !== null
    ? String(value.description || value.explanation || value.detail || value.summary || '').trim()
    : '';
  return { title, detail };
};

const normalizeStudyNote = (value, index) => {
  if (!value) return null;
  if (typeof value === 'string') {
    const text = value.trim();
    return text ? { title: `Study note ${index + 1}`, text } : null;
  }

  if (typeof value !== 'object') return null;
  const text = String(value.text || value.summary || value.detail || value.description || value.note || value.title || '').trim();
  if (!text) return null;
  return {
    title: String(value.title || value.heading || `Study note ${index + 1}`).trim(),
    text,
    source: value.source || value.sourceReference || null
  };
};

const normalizeFlashcard = (value, index) => {
  if (!value || typeof value !== 'object') return null;
  const question = String(value.question || value.prompt || value.front || value.title || '').trim();
  const answer = String(value.answer || value.response || value.back || value.text || value.explanation || '').trim();
  if (!question || !answer) return null;
  const rawTags = Array.isArray(value.tags) ? value.tags : [];
  return {
    id: String(value.id || `${question}-${index}`).trim(),
    question,
    answer,
    topic: String(value.topic || value.concept || '').trim(),
    tags: rawTags.map(toText).filter(Boolean).slice(0, 4)
  };
};

const parseTimeValue = (value, { milliseconds = false } = {}) => {
  if (value === undefined || value === null || value === '') return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? Math.max(0, milliseconds ? value / 1000 : value) : 0;

  const raw = String(value).trim();
  if (!raw) return 0;
  if (/^\d+(?:\.\d+)?$/.test(raw)) {
    const numeric = Number(raw);
    return Number.isFinite(numeric) ? Math.max(0, milliseconds ? numeric / 1000 : numeric) : 0;
  }

  const parts = raw.split(':').map((part) => Number(part.replace(',', '.')));
  if (parts.length >= 2 && parts.length <= 3 && parts.every(Number.isFinite)) {
    const seconds = parts.reduce((total, part) => (total * 60) + part, 0);
    return Math.max(0, seconds);
  }

  return 0;
};

const normalizeTimedLyric = (line, index) => {
  if (!line) return null;
  if (typeof line === 'string') {
    return { time: index * 8, text: line.trim() };
  }

  const timeSource = line.time ?? line.start ?? line.startTime ?? line.timestamp;
  const time = timeSource !== undefined
    ? parseTimeValue(timeSource)
    : parseTimeValue(line.timeMs ?? line.startMs ?? line.timestampMs, { milliseconds: true });
  const endSource = line.end ?? line.endTime ?? line.stop ?? line.finish;
  const endTime = endSource !== undefined
    ? parseTimeValue(endSource)
    : parseTimeValue(line.endMs ?? line.stopMs ?? line.finishMs, { milliseconds: true });
  const text = toText(line);
  if (!text) return null;
  return {
    time,
    endTime: endTime || undefined,
    section: String(line.section || line.label || '').trim(),
    text
  };
};

const isPlaceholderTimedLyricText = (text = '') => {
  const normalized = String(text).trim().toLowerCase();
  return /^(?:0+|0[:. ]0+|00[:. ]00|000+)$/.test(normalized);
};

const sanitizeTimedLyrics = (lines = []) => {
  const normalized = asArray(lines).map(normalizeTimedLyric).filter(Boolean);
  const hasPositiveTiming = normalized.some((line) => Number(line.time) > 0);
  let lastAcceptedTime = -Infinity;

  return normalized.filter((line, index) => {
    const time = Number(line.time);
    if (!Number.isFinite(time)) return false;
    if (isPlaceholderTimedLyricText(line.text)) return false;
    if (time < lastAcceptedTime) return false;
    lastAcceptedTime = time;
    return true;
  });
};

const normalizeLyricOffset = (...values) => {
  const value = values.find((candidate) => candidate !== undefined && candidate !== null && candidate !== '');
  const offset = Number(value);
  return Number.isFinite(offset) ? offset : 0;
};

const splitStaticLyrics = (lyrics = '') => lyrics
  .split('\n')
  .map((line) => line.trim())
  .filter(Boolean)
  .map((text, index) => ({ time: index * 8, text }));

const firstText = (...values) => values
  .map((value) => (typeof value === 'string' ? value.trim() : ''))
  .find(Boolean) || '';

const getSelectedVariant = (generation = {}) => {
  const variants = asArray(generation.variants || generation.audioVariants || generation.songVariants);
  if (!variants.length) return {};

  if (generation.selectedVariant && typeof generation.selectedVariant === 'object') {
    return generation.selectedVariant;
  }

  const selectedId = generation.selectedVariantId || generation.selectedVariant || generation.selectedVariantKey;
  if (!selectedId) return variants[0] || {};

  return variants.find((variant) => (
    variant?.id === selectedId
    || variant?.key === selectedId
    || variant?.name === selectedId
    || variant?.label === selectedId
  )) || variants[0] || {};
};

export const extractSongLyrics = (song = {}) => {
  const generation = song.contentGeneration || {};
  const learning = song.learningMetadata || {};
  const education = generation.educationalMetadata || generation.learningMetadata || {};
  const selectedVariant = getSelectedVariant(generation);

  return firstText(
    song.lyrics,
    learning.lyrics,
    learning.staticLyrics,
    learning.fullLyrics,
    generation.lyrics,
    generation.staticLyrics,
    generation.fullLyrics,
    generation.finalLyrics,
    generation.generatedLyrics,
    generation.selectedLyrics,
    selectedVariant.lyrics,
    selectedVariant.fullLyrics,
    selectedVariant.finalLyrics,
    education.lyrics
  );
};

export const getSongLearningMetadata = (song = {}) => {
  const generation = song.contentGeneration || {};
  const learning = song.learningMetadata || {};
  const review = generation.audioReview || generation.review || {};
  const education = generation.educationalMetadata || generation.learningMetadata || {};
  const lyrics = extractSongLyrics(song);

  const concepts = firstArray(
    learning.keyConcepts,
    learning.concepts,
    generation.keyConcepts,
    generation.keyLearningConcepts,
    generation.learningConcepts,
    generation.concepts,
    education.keyConcepts,
    education.learningConcepts,
    review.keyConcepts
  ).map(normalizeConcept).filter(Boolean).slice(0, 5);

  const timedLyrics = sanitizeTimedLyrics(firstTimedLyricArray(
    learning.timedLyrics,
    learning.lyricTimings,
    generation.timedLyrics,
    generation.lyricTimings,
    generation.lyricsTimed,
    generation.synchronizedLyrics,
    song.timedLyrics
  ));

  const lyricOffsetSeconds = normalizeLyricOffset(
    learning.lyricOffsetSeconds,
    learning.lyricsOffsetSeconds,
    learning.lyricSyncOffset,
    generation.lyricOffsetSeconds,
    generation.lyricsOffsetSeconds,
    generation.lyricSyncOffset,
    song.lyricOffsetSeconds
  );

  const studyNotes = firstArray(
    learning.studyNotes,
    learning.notes,
    generation.studyNotes,
    generation.learningNotes,
    education.studyNotes,
    review.studyNotes
  ).map(normalizeStudyNote).filter(Boolean).slice(0, 6);

  const flashcards = firstArray(
    learning.flashcards,
    learning.quizCards,
    learning.revisionCards,
    generation.flashcards,
    generation.quizCards,
    generation.revisionCards,
    education.flashcards,
    review.flashcards
  ).map(normalizeFlashcard).filter(Boolean).slice(0, 8);

  const progress = learning.progress && typeof learning.progress === 'object'
    ? learning.progress
    : null;

  return {
    concepts,
    timedLyrics: timedLyrics.length ? timedLyrics : splitStaticLyrics(lyrics),
    lyricOffsetSeconds,
    studyNotes,
    flashcards,
    progress,
    hasConcepts: concepts.length > 0,
    hasStudyNotes: studyNotes.length > 0,
    hasFlashcards: flashcards.length > 0,
    hasProgress: Boolean(progress),
    hasTimedLyrics: timedLyrics.length > 0
  };
};

export const getActiveLyricIndex = (timedLyrics = [], currentTime = 0) => {
  const lines = sanitizeTimedLyrics(timedLyrics);
  if (!lines.length) return -1;
  const time = Number(currentTime) || 0;

  const activeIndex = lines.findIndex((line, index) => {
    const next = lines[index + 1];
    const endTime = Number(line.endTime ?? next?.time ?? Infinity);
    return time >= Number(line.time || 0) && time < endTime;
  });

  if (activeIndex >= 0) return activeIndex;
  return time >= Number(lines[lines.length - 1].time || 0) ? lines.length - 1 : 0;
};
